import React, { useCallback, useEffect, useMemo, useState } from "react";

import { DialogShell } from "./APIKeyDialogs.jsx";

/* ==================================================================
 * APIKeyPolicies
 * -----------------------------------------------------------------
 * Usage-policy management surface for a single API client. Backend
 * exposes:
 *   GET    /api/ApiClient/{apiClientId}/Policies
 *   POST   /api/ApiClient/{apiClientId}/Policies
 *   PUT    /api/ApiClient/{apiClientId}/Policies/{policyId}
 *   DELETE /api/ApiClient/{apiClientId}/Policies/{policyId}
 *
 * A policy is uniquely identified by (PolicyDefinitionId, RequestType).
 * We pre-filter used slots out of the Add form so the operator can't
 * try to create a duplicate; the server still validates.
 *
 * Reset period is stored as an integer number of days on the backend
 * (0 = never auto-resets). To keep the intent unambiguous for admins,
 * the form splits this into an "Auto-reset" toggle + days input; when
 * the toggle is off we serialize ResetPeriod: 0.
 *
 * Response casing: Web API 2 with the shared JSON formatter can serve
 * either camelCase or PascalCase depending on which controller last
 * ran on the worker, so every read tolerates both.
 * ================================================================== */


const POLICY_DEFINITIONS_FALLBACK = [
  { value: 1, label: "Request count" },
  { value: 2, label: "Response count" },
];

const POLICY_REQUEST_TYPES_FALLBACK = [
  { value: 1, label: "User" },
  { value: 2, label: "Content" },
];

let POLICY_DEFINITIONS = POLICY_DEFINITIONS_FALLBACK.slice();
let POLICY_REQUEST_TYPES = POLICY_REQUEST_TYPES_FALLBACK.slice();
let policyOptionsFetchPromise = null;

/* "RequestCount" -> "Request count". Keeps display strings readable
 * regardless of what the C# enum member is named. */
const humanizeEnumName = raw =>
  String(raw || "")
    .replace(/[_\s]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/^./, c => c.toUpperCase())
    .replace(/\s+([A-Z])/g, (_, c) => ` ${c.toLowerCase()}`);

/* Convert the backend KeyValueDto array into the FE's { value, label }
 * shape. Tolerates camel/PascalCase and a couple of alternate field
 * names so the FE keeps working if the DTO ever gets refactored. */
const normalizeEnumPayload = data => {
  const arr = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.data)
        ? data.data
        : [];
  return arr
    .map(raw => {
      if (!raw || typeof raw !== "object") return null;
      const value = Number(raw.Key ?? raw.key ?? raw.value ?? raw.Value);
      if (!Number.isFinite(value)) return null;
      const rawLabel =
        raw.Value ?? raw.value ?? raw.Name ?? raw.name ?? raw.Label ?? raw.label;
      const label =
        typeof rawLabel === "string" && rawLabel.trim()
          ? humanizeEnumName(rawLabel)
          : String(value);
      return { value, label };
    })
    .filter(Boolean);
};

const fetchPolicyOptions = apiRequest => {
  if (policyOptionsFetchPromise) return policyOptionsFetchPromise;
  policyOptionsFetchPromise = (async () => {
    const [defsResult, typesResult] = await Promise.allSettled([
      apiRequest("/api/ApiClient/GetApiPolicyDefinitions", { method: "GET" }),
      apiRequest("/api/ApiClient/GetRequestTypes", { method: "GET" }),
    ]);
    if (defsResult.status === "fulfilled") {
      const defs = normalizeEnumPayload(defsResult.value);
      if (defs.length) POLICY_DEFINITIONS = defs;
    }
    if (typesResult.status === "fulfilled") {
      const types = normalizeEnumPayload(typesResult.value);
      if (types.length) POLICY_REQUEST_TYPES = types;
    }
  })();
  return policyOptionsFetchPromise;
};

const definitionLabel = value => {
  const match = POLICY_DEFINITIONS.find(o => o.value === Number(value));
  return match ? match.label : "\u2014";
};

const requestTypeLabel = value => {
  const match = POLICY_REQUEST_TYPES.find(o => o.value === Number(value));
  return match ? match.label : "Unknown";
};

const formatResetPeriod = days => {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return "Never";
  if (n === 1) return "Every day";
  return `Every ${n} days`;
};

const formatDateTime = value => {
  if (!value) return "\u2014";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "\u2014";
  return d.toLocaleString();
};

const normalizePolicy = raw => {
  const p = raw || {};
  return {
    id: p.id ?? p.Id,
    apiClientId: p.apiClientId ?? p.ApiClientId,
    policyDefinitionId: Number(
      p.policyDefinitionId ?? p.PolicyDefinitionId ?? 0,
    ),
    policyName: p.policyName ?? p.PolicyName ?? "",
    requestType: Number(p.requestType ?? p.RequestType ?? 0),
    limit: Number(p.limit ?? p.Limit ?? 0),
    usedCount: Number(p.usedCount ?? p.UsedCount ?? 0),
    remaining: Number(p.remaining ?? p.Remaining ?? 0),
    resetPeriod: Number(p.resetPeriod ?? p.ResetPeriod ?? 0),
    isActive: (p.isActive ?? p.IsActive) !== false,
    lastResetOn: p.lastResetOn ?? p.LastResetOn ?? null,
  };
};

const policiesBase = clientId =>
  `/api/ApiClient/${encodeURIComponent(clientId)}/Policies`;

/* Small "+" glyph used on the primary Add-policy button so the affordance
 * mirrors the top-level SPA's "Add API Client" button. Kept inline (no
 * shared icon component) to match the sibling pattern in APIKeyManager.jsx. */
const PlusIcon = () => (
  <span
    className="api-key-manager__primary-action-icon"
    aria-hidden="true"
  >
    <svg viewBox="0 0 16 16" width="14" height="14">
      <path
        d="M8 2.75 V13.25 M2.75 8 H13.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  </span>
);

/* ==================================================================
 * Add
 * ================================================================== */
const AddPolicyDialog = ({ takenSlots, onSubmit, onClose }) => {
  /* Slot combinations already configured on the server. The user cannot
   * create a duplicate (server rejects it, and the shape strongly implies
   * one policy per pair), so we drop those pairs from the pickers. */
  const availableDefinitions = useMemo(
    () =>
      POLICY_DEFINITIONS.filter(def =>
        POLICY_REQUEST_TYPES.some(
          type => !takenSlots.has(`${def.value}:${type.value}`),
        ),
      ),
    [takenSlots],
  );

  const [policyDefinitionId, setPolicyDefinitionId] = useState(
    availableDefinitions[0]?.value ?? POLICY_DEFINITIONS[0].value,
  );

  const availableTypes = useMemo(
    () =>
      POLICY_REQUEST_TYPES.filter(
        type => !takenSlots.has(`${policyDefinitionId}:${type.value}`),
      ),
    [policyDefinitionId, takenSlots],
  );

  const [requestType, setRequestType] = useState(
    availableTypes[0]?.value ?? POLICY_REQUEST_TYPES[0].value,
  );

  /* Keep requestType valid whenever the definition changes and the
   * previously-selected type is no longer available. */
  useEffect(() => {
    if (
      availableTypes.length &&
      !availableTypes.some(t => t.value === requestType)
    ) {
      setRequestType(availableTypes[0].value);
    }
  }, [availableTypes, requestType]);

  const [limit, setLimit] = useState("100");
  const [autoReset, setAutoReset] = useState(false);
  const [resetPeriod, setResetPeriod] = useState("30");
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async e => {
    e.preventDefault();
    setError("");
    const limitNum = parseInt(limit, 10);
    if (!Number.isFinite(limitNum) || limitNum < 1) {
      setError("Limit must be a whole number greater than or equal to 1.");
      return;
    }
    let resetPeriodNum = 0;
    if (autoReset) {
      resetPeriodNum = parseInt(resetPeriod, 10);
      if (!Number.isFinite(resetPeriodNum) || resetPeriodNum < 1) {
        setError(
          "Reset period must be a whole number of days greater than or equal to 1 when auto-reset is enabled.",
        );
        return;
      }
    }
    setBusy(true);
    try {
      await onSubmit({
        policyDefinitionId: Number(policyDefinitionId),
        requestType: Number(requestType),
        limit: limitNum,
        resetPeriod: resetPeriodNum,
        isActive,
      });
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell title="Add usage policy" onClose={onClose}>
      <form className="api-key-manager-dialog__body" onSubmit={submit}>
        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Policy definition{" "}
            <span className="api-key-manager-dialog__required">*</span>
          </span>
          <select
            value={policyDefinitionId}
            onChange={e =>
              setPolicyDefinitionId(parseInt(e.target.value, 10))
            }
            disabled={busy}
          >
            {availableDefinitions.map(def => (
              <option key={def.value} value={def.value}>
                {def.label}
              </option>
            ))}
          </select>
        </label>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Request type{" "}
            <span className="api-key-manager-dialog__required">*</span>
          </span>
          <select
            value={requestType}
            onChange={e => setRequestType(parseInt(e.target.value, 10))}
            disabled={busy || availableTypes.length === 0}
          >
            {availableTypes.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Limit{" "}
            <span className="api-key-manager-dialog__required">*</span>
          </span>
          <input
            type="number"
            min="1"
            step="1"
            value={limit}
            onChange={e => setLimit(e.target.value)}
            required
            disabled={busy}
          />
        </label>

        <label className="api-key-manager-dialog__field api-key-manager-dialog__field--inline">
          <input
            type="checkbox"
            checked={autoReset}
            onChange={e => setAutoReset(e.target.checked)}
            disabled={busy}
          />
          <span className="api-key-manager-dialog__label">
            Auto-reset usage
          </span>
        </label>

        {autoReset && (
          <label className="api-key-manager-dialog__field">
            <span className="api-key-manager-dialog__label">
              Reset every (days){" "}
              <span className="api-key-manager-dialog__required">*</span>
            </span>
            <input
              type="number"
              min="1"
              step="1"
              value={resetPeriod}
              onChange={e => setResetPeriod(e.target.value)}
              required
              disabled={busy}
            />
          </label>
        )}

        <label className="api-key-manager-dialog__field api-key-manager-dialog__field--inline">
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            disabled={busy}
          />
          <span className="api-key-manager-dialog__label">Active</span>
        </label>

        {error && (
          <p className="api-key-manager-dialog__error" role="alert">
            {error}
          </p>
        )}

        <div className="api-key-manager-dialog__actions">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy || availableTypes.length === 0}
          >
            {busy ? "Creating\u2026" : "Create policy"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};

/* ==================================================================
 * Edit
 * -----------------------------------------------------------------
 * Only Limit, ResetPeriod, and IsActive are mutable per Bhavin's
 * UpdateApiClientPolicyRequest DTO. PolicyDefinitionId and RequestType
 * are shown for context but locked.
 * ================================================================== */
const EditPolicyDialog = ({ policy, onSubmit, onClose }) => {
  const [limit, setLimit] = useState(String(policy.limit ?? 0));
  const [autoReset, setAutoReset] = useState((policy.resetPeriod ?? 0) > 0);
  const [resetPeriod, setResetPeriod] = useState(
    policy.resetPeriod > 0 ? String(policy.resetPeriod) : "30",
  );
  const [isActive, setIsActive] = useState(policy.isActive !== false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async e => {
    e.preventDefault();
    setError("");
    const limitNum = parseInt(limit, 10);
    if (!Number.isFinite(limitNum) || limitNum < 1) {
      setError("Limit must be a whole number greater than or equal to 1.");
      return;
    }
    let resetPeriodNum = 0;
    if (autoReset) {
      resetPeriodNum = parseInt(resetPeriod, 10);
      if (!Number.isFinite(resetPeriodNum) || resetPeriodNum < 1) {
        setError(
          "Reset period must be a whole number of days greater than or equal to 1 when auto-reset is enabled.",
        );
        return;
      }
    }
    setBusy(true);
    try {
      await onSubmit({
        limit: limitNum,
        resetPeriod: resetPeriodNum,
        isActive,
      });
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell title="Edit usage policy" onClose={onClose}>
      <form className="api-key-manager-dialog__body" onSubmit={submit}>
        <p className="api-key-manager-dialog__muted">
          Definition and Request type are locked once the policy exists.
          Delete this policy and add a new one to change either.
        </p>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Policy definition
          </span>
          <select
            value={policy.policyDefinitionId}
            disabled
            aria-readonly="true"
          >
            <option value={policy.policyDefinitionId}>
              {policy.policyName ||
                definitionLabel(policy.policyDefinitionId)}
            </option>
          </select>
        </label>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">Request type</span>
          <select
            value={policy.requestType}
            disabled
            aria-readonly="true"
          >
            <option value={policy.requestType}>
              {requestTypeLabel(policy.requestType)}
            </option>
          </select>
        </label>

        <label className="api-key-manager-dialog__field">
          <span className="api-key-manager-dialog__label">
            Limit{" "}
            <span className="api-key-manager-dialog__required">*</span>
          </span>
          <input
            type="number"
            min="1"
            step="1"
            value={limit}
            onChange={e => setLimit(e.target.value)}
            required
            disabled={busy}
          />
        </label>

        <label className="api-key-manager-dialog__field api-key-manager-dialog__field--inline">
          <input
            type="checkbox"
            checked={autoReset}
            onChange={e => setAutoReset(e.target.checked)}
            disabled={busy}
          />
          <span className="api-key-manager-dialog__label">
            Auto-reset usage
          </span>
        </label>

        {autoReset && (
          <label className="api-key-manager-dialog__field">
            <span className="api-key-manager-dialog__label">
              Reset every (days){" "}
              <span className="api-key-manager-dialog__required">*</span>
            </span>
            <input
              type="number"
              min="1"
              step="1"
              value={resetPeriod}
              onChange={e => setResetPeriod(e.target.value)}
              required
              disabled={busy}
            />
          </label>
        )}

        <label className="api-key-manager-dialog__field api-key-manager-dialog__field--inline">
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            disabled={busy}
          />
          <span className="api-key-manager-dialog__label">Active</span>
        </label>

        {error && (
          <p className="api-key-manager-dialog__error" role="alert">
            {error}
          </p>
        )}

        <div className="api-key-manager-dialog__actions">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy}
          >
            {busy ? "Saving\u2026" : "Save changes"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};

/* ==================================================================
 * Delete confirm
 * ================================================================== */
const DeletePolicyDialog = ({ policy, onConfirm, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const confirm = async () => {
    setError("");
    setBusy(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell title="Delete usage policy?" onClose={onClose}>
      <div className="api-key-manager-dialog__body">
        <p>
          Delete the{" "}
          <strong>
            {policy.policyName || definitionLabel(policy.policyDefinitionId)}
          </strong>{" "}
          policy for{" "}
          <strong>{requestTypeLabel(policy.requestType)}</strong>{" "}
          requests? The client will no longer be limited on this
          combination and current usage counters will be discarded.
        </p>
        {error && (
          <p className="api-key-manager-dialog__error" role="alert">
            {error}
          </p>
        )}
        <div className="api-key-manager-dialog__actions">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn api-key-manager-dialog__btn-danger"
            onClick={confirm}
            disabled={busy}
          >
            {busy ? "Deleting\u2026" : "Delete policy"}
          </button>
        </div>
      </div>
    </DialogShell>
  );
};

/* ==================================================================
 * Main list dialog
 * -----------------------------------------------------------------
 * Shown when the operator clicks "Policies" on a row. Fetches the
 * policies for the given client, renders a compact usage table, and
 * exposes Add / Edit / Delete affordances.
 *
 * `apiRequest` and `showToast` are injected from the parent so we
 * reuse the exact fetch wrapper (timeout, abort, error normalization)
 * and toast styling used by the surrounding SPA.
 * ================================================================== */
export const APIKeyPoliciesDialog = ({
  client,
  apiRequest,
  showToast,
  onClose,
}) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subDialog, setSubDialog] = useState(null);
  /* Bumped once the lookup endpoints resolve, so the arrays populated
   * by `fetchPolicyOptions` (module scope) flow into the next render. */
  const [enumsVersion, setEnumsVersion] = useState(0);

  const clientId = client.id;

  useEffect(() => {
    let cancelled = false;
    fetchPolicyOptions(apiRequest).then(() => {
      if (!cancelled) setEnumsVersion(v => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [apiRequest]);

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(policiesBase(clientId), {
        method: "GET",
      });
      const list = Array.isArray(data) ? data.map(normalizePolicy) : [];
      setPolicies(list);
    } catch (err) {
      if (err?.category === "aborted") return;
      showToast(
        "error",
        "Could not load policies",
        err?.message || "Something went wrong.",
      );
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, clientId, showToast]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const takenSlots = useMemo(() => {
    const s = new Set();
    for (const p of policies) {
      s.add(`${p.policyDefinitionId}:${p.requestType}`);
    }
    return s;
  }, [policies]);

  const totalSlots = useMemo(
    () => POLICY_DEFINITIONS.length * POLICY_REQUEST_TYPES.length,
    [enumsVersion],
  );
  const canAdd = takenSlots.size < totalSlots;

  const handleCreate = async form => {
    await apiRequest(policiesBase(clientId), {
      method: "POST",
      body: JSON.stringify({
        policyDefinitionId: form.policyDefinitionId,
        requestType: form.requestType,
        limit: form.limit,
        resetPeriod: form.resetPeriod,
        isActive: form.isActive,
      }),
    });
    setSubDialog(null);
    showToast(
      "success",
      "Policy created",
      `${definitionLabel(form.policyDefinitionId)} for ${requestTypeLabel(
        form.requestType,
      )} was added.`,
    );
    fetchPolicies();
  };

  const handleUpdate = async (policy, form) => {
    await apiRequest(
      `${policiesBase(clientId)}/${encodeURIComponent(policy.id)}`,
      {
        method: "PUT",
        body: JSON.stringify({
          limit: form.limit,
          resetPeriod: form.resetPeriod,
          isActive: form.isActive,
        }),
      },
    );
    setSubDialog(null);
    showToast(
      "success",
      "Policy updated",
      `${policy.policyName || definitionLabel(policy.policyDefinitionId)} for ${requestTypeLabel(
        policy.requestType,
      )} was saved.`,
    );
    fetchPolicies();
  };

  const handleDelete = async policy => {
    await apiRequest(
      `${policiesBase(clientId)}/${encodeURIComponent(policy.id)}`,
      { method: "DELETE" },
    );
    setSubDialog(null);
    showToast(
      "success",
      "Policy deleted",
      `${policy.policyName || definitionLabel(policy.policyDefinitionId)} for ${requestTypeLabel(
        policy.requestType,
      )} was removed.`,
    );
    fetchPolicies();
  };

  return (
    <>
      <DialogShell
        title={`Usage policies for ${client.clientName || "API client"}`}
        onClose={onClose}
        size="lg"
      >
        <div className="api-key-manager-dialog__body api-key-policies">
          <div className="api-key-policies__toolbar">
            <p className="api-key-manager-dialog__muted api-key-policies__intro">
              Cap how many requests this client may make or how many
              responses it can receive, per traffic type. Any combination
              without an active policy is unlimited.
            </p>
            <button
              type="button"
              className="btn btn--primary api-key-manager__primary-action"
              onClick={() => setSubDialog({ type: "add" })}
              disabled={loading || !canAdd}
              title={
                canAdd
                  ? undefined
                  : "All policy slots are configured for this client."
              }
            >
              <PlusIcon />
              Add policy
            </button>
          </div>

          {loading && policies.length === 0 ? (
            <p className="api-key-policies__empty">
              Loading policies&hellip;
            </p>
          ) : policies.length === 0 ? (
            <div className="api-key-policies__empty api-key-policies__empty--cta">
              <p className="api-key-policies__empty-title">
                No usage policies yet
              </p>
              <p className="api-key-policies__empty-body">
                This client can call the API without any per-day or
                per-week caps. Use &ldquo;Add policy&rdquo; above to start
                metering usage.
              </p>
            </div>
          ) : (
            <div className="api-key-policies__table-scroll">
              <table className="api-key-policies__table">
                <thead>
                  <tr>
                    <th scope="col">Definition</th>
                    <th scope="col">Type</th>
                    <th
                      scope="col"
                      className="api-key-policies__col-num"
                    >
                      Usage
                    </th>
                    <th scope="col">Reset</th>
                    <th scope="col">Status</th>
                    <th scope="col">Last reset</th>
                    <th
                      scope="col"
                      className="api-key-manager__col-actions"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map(p => {
                    const remaining =
                      p.remaining !== 0
                        ? p.remaining
                        : Math.max(0, p.limit - p.usedCount);
                    const usagePct = p.limit > 0
                      ? Math.min(100, Math.round((p.usedCount / p.limit) * 100))
                      : 0;
                    const usageTone =
                      usagePct >= 90 ? "danger" :
                      usagePct >= 70 ? "warn" : "ok";
                    return (
                      <tr key={p.id}>
                        <td data-label="Definition">
                          {p.policyName ||
                            definitionLabel(p.policyDefinitionId)}
                        </td>
                        <td data-label="Type">
                          {requestTypeLabel(p.requestType)}
                        </td>
                        <td
                          className="api-key-policies__col-num"
                          data-label="Usage"
                          title={`${remaining} remaining`}
                        >
                          <span className="api-key-policies__usage">
                            <span className="api-key-policies__usage-used">
                              {p.usedCount}
                            </span>
                            <span className="api-key-policies__usage-sep">
                              /
                            </span>
                            <span className="api-key-policies__usage-limit">
                              {p.limit}
                            </span>
                          </span>
                          <span
                            className={`api-key-policies__usage-bar api-key-policies__usage-bar--${usageTone}`}
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={usagePct}
                            aria-label={`${usagePct}% of limit used`}
                          >
                            <span
                              className="api-key-policies__usage-bar-fill"
                              style={{ width: `${usagePct}%` }}
                            />
                          </span>
                        </td>
                        <td data-label="Reset">
                          {formatResetPeriod(p.resetPeriod)}
                        </td>
                        <td data-label="Status">
                          <span
                            className={`api-key-manager__badge api-key-manager__badge--${
                              p.isActive ? "active" : "revoked"
                            }`}
                          >
                            {p.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td data-label="Last reset">
                          {formatDateTime(p.lastResetOn)}
                        </td>
                        <td
                          className="api-key-manager__col-actions"
                          data-label="Actions"
                        >
                          <span className="api-key-manager__row-actions">
                            <button
                              type="button"
                              className="api-key-manager__row-action"
                              onClick={() =>
                                setSubDialog({ type: "edit", policy: p })
                              }
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="api-key-manager__row-action api-key-manager__row-action--danger"
                              onClick={() =>
                                setSubDialog({ type: "delete", policy: p })
                              }
                            >
                              Delete
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="api-key-manager-dialog__actions">
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </DialogShell>

      {subDialog?.type === "add" && (
        <AddPolicyDialog
          takenSlots={takenSlots}
          onSubmit={handleCreate}
          onClose={() => setSubDialog(null)}
        />
      )}
      {subDialog?.type === "edit" && subDialog.policy && (
        <EditPolicyDialog
          policy={subDialog.policy}
          onSubmit={form => handleUpdate(subDialog.policy, form)}
          onClose={() => setSubDialog(null)}
        />
      )}
      {subDialog?.type === "delete" && subDialog.policy && (
        <DeletePolicyDialog
          policy={subDialog.policy}
          onConfirm={() => handleDelete(subDialog.policy)}
          onClose={() => setSubDialog(null)}
        />
      )}
    </>
  );
};

export default APIKeyPoliciesDialog;
